import { api, apiClient } from "../lib";
import type { AccessioningPanelSelection } from "./accessioningPanels.service";
import type { ExtractionJob } from "../types";
import { appendCaseFilters, type CaseFilters } from "./cases.service";
import type { FragmentCountQualifier } from "../utils/fragmentCount";
import { deduplicateDiagnosisCategoriesByName } from "../utils/diagnosisCategories";
import type { WorkListStainSubmission } from "./workList.service";
import type { DateOnlyString } from "../utils/dateOnly";
import { getApiErrorStatus } from "../utils/apiError";
import { normalizeDateOnlyQueryRange } from "./dateOnlyQuery";
import {
  getResponseDownloadFilename,
  triggerBlobDownload,
} from "../utils/downloadFilename";

export interface InsufficientTissueSpecimen {
  id: string;
  number: string;
  insufficientTissue: boolean;
  insufficientTissueVersion: number;
  insufficientTissueChangedBy?: string | null;
  insufficientTissueChangedAt?: string | null;
}

export interface InsufficientTissuePreview {
  version: number;
  materialFingerprint: string;
  slideCount: number;
  heCount: number;
}

export interface InsufficientTissueResult {
  specimenId: string;
  insufficientTissue: boolean;
  version: number;
  removedSlideIds: string[];
  currentStatus: string;
}

export interface InsufficientTissueTarget {
  caseId: string;
  specimenId: string;
  stage: "grossing" | "embedding" | "cutting";
}

export interface InsufficientTissueConfirmation
  extends InsufficientTissueTarget {
  mode: "all" | "he_only";
  expectedVersion: number;
  materialFingerprint: string;
  requestId: string;
}

function insufficientTissuePath(target: InsufficientTissueTarget): string {
  return `/workflows/${target.stage}/${target.caseId}/specimens/${target.specimenId}/insufficient-tissue`;
}

export function insufficientTissueError(error: unknown): string {
  const status = getApiErrorStatus(error);
  if (status === 403)
    return "You do not have permission to change this specimen.";
  if (status === 409)
    return "The case or its material changed, is locked, or has protected testing history. Reload and review it before continuing.";
  if (status === 401) return "Your session ended. Sign in again to continue.";
  return "The specimen could not be updated. Your changes were not saved.";
}

export const insufficientTissueService = {
  setFlag: async (
    input: InsufficientTissueTarget & {
      insufficientTissue: boolean;
      expectedVersion: number;
    }
  ): Promise<InsufficientTissueResult> => {
    return api.put(insufficientTissuePath(input), {
      insufficientTissue: input.insufficientTissue,
      expectedVersion: input.expectedVersion,
    });
  },
  preview: async (
    input: InsufficientTissueTarget
  ): Promise<InsufficientTissuePreview> => {
    return api.get(insufficientTissuePath(input));
  },
  confirm: async (
    input: InsufficientTissueConfirmation
  ): Promise<InsufficientTissueResult> => {
    return api.post(`${insufficientTissuePath(input)}/confirm`, {
      mode: input.mode,
      expectedVersion: input.expectedVersion,
      materialFingerprint: input.materialFingerprint,
      requestId: input.requestId,
    });
  },
};

// ==================== ACCESSIONING ====================

// ==================== TYPES ====================

export interface DraftCaseResult {
  caseId: string;
  caseNumber: string;
}

export interface SpecimenIdentifierPreview {
  changes: Array<{ id: string; before: string; after: string }>;
  printedMaterialTypes: string[];
}

export interface AccessioningData {
  specimenIdentifierEdits?: {
    mode: "preview" | "save";
    confirmLabelReprint?: boolean;
    expectedIdentifiers?: Record<string, string>;
  };
  patient: {
    firstName: string;
    lastName: string;
    dateOfBirth: DateOnlyString | null;
    sex: string;
    medicalRecordNumber: string;
    mobilePhone?: string;
    homePhone?: string;
    workPhone?: string;
    email?: string;
    address?: {
      address: string;
      city: string;
      state: string;
      zipCode: string;
    };
  };
  case: {
    clientId?: string;
    procedureId?: string;
    procedureName?: string;
    dateCollected?: DateOnlyString | null;
    clinicalHistoryCodes?: string[];
    clinicalHistoryNotes?: string;
    indications?: string;
    impressions?: string;
    pathologistId?: string;
    billingScenarioId?: string;
    // Rush flag - physical RUSH stamp detected on requisition (expedited processing)
    isRush?: boolean;
    rushReason?: string;
    // High Priority flag - clinical keywords detected (mass, tumor, cancer) (flagged for attention)
    isHighPriority?: boolean;
    highPriorityReason?: string;
    primaryInsurance?: any;
    secondaryInsurance?: any;
    noPatientBillingAndInsuranceInfoRequired?: boolean;
    externalCaseNumber?: string;
    isGrossingOnly?: boolean;
  };
  specimens: Array<{
    /** Set for specimens already persisted on the case; omitted for newly added ones. */
    id?: string;
    number: string;
    sourceId?: string;
    subsiteId?: string;
    collectionMethod?: string;
    material?: string;
    findings?: string;
    ruleOuts?: string[];
    comments?: string;
    gross?: string;
    grossCodeId?: string;
    blockCount?: number;
  }>;
  extractedData?: any;
  /** If true, create practice mappings (physician ↔ practice, surgery center ↔ practice) on completion */
  createMappings?: boolean;
  /** Slide completion status for external cases (pass-through, consultation). Format: {specimenNumber: {stainId: isCompleted}} */
  slideCompletions?: Record<string, Record<string, boolean>>;
  /** Manually added/removed stains per specimen. Format: {specimenIndex: {added: [{id, code}], removed: [stainId]}} */
  manualStains?: Record<
    number,
    {
      added: Array<{
        id: string;
        selectionId?: string;
        code: string;
        isSystemGenerated?: boolean;
      }>;
      removed: string[];
    }
  >;
  /** Work list stain overrides — when a work list match is found, these stains replace source defaults */
  workListStains?: WorkListStainSubmission[];
  /** AI-extracted stains per specimen — replace source defaults when present. isPreProcessed
   * (received already stained) is only honoured for slides_only / blocks_and_slides materials. */
  extractedStains?: Array<{
    specimenNumber: string;
    stainId: string;
    stainCode: string;
    isPreProcessed: boolean;
  }>;
  /** Final stain list visible to the user. When present, this is authoritative at completion. */
  resolvedStains?: Record<
    number,
    Array<{
      stainId: string;
      selectionId?: string;
      isCompleted: boolean;
      panelSelectionKey?: string;
      panelComponentId?: string;
    }>
  >;
  /** Server-authoritative panel/reflex selections, expanded once for every specimen block. */
  panelSelections?: Record<number, AccessioningPanelSelection[]>;
  /** ID of the matched expected case (from work list integration) */
  expectedCaseId?: string;
  /** Documents to link to the case (S3 keys and metadata) - stored in case_files table */
  documents?: Array<{
    s3Key: string;
    fileName?: string;
    mimeType?: string;
    size?: number;
  }>;
}

export interface CaseLockResult {
  success: boolean;
  expiresAt?: Date;
  lockedBy?: string;
}

export interface WorkflowQueueCase {
  id: string;
  caseNumber: string;
  parentCaseNumber?: string | null;
  parentCase?: { id: string; caseNumber: string } | null;
  externalCaseNumber?: string;
  dateReceived: string;
  isBlocked?: boolean;
  isRush?: boolean;
  isHighPriority?: boolean;
  patient?: {
    firstName: string;
    lastName: string;
    dateOfBirth?: string;
    medicalRecordNumber?: string;
  };
  pathologist?: {
    fullName: string;
  };
  currentStatus?: {
    id: string;
    name: string;
  };
  workflow?: {
    id: string;
    name?: string;
    accessioningType?: string;
  };
  facility?: {
    id: string;
    name: string;
  };
  procedure?: {
    id: string;
    name: string;
  };
  specimenCount?: number;
  blockCount?: number;
  lockedBy?: string;
  lockedByName?: string;
  isAdditionalStain?: boolean;
  slides?: Array<{
    id: string;
    isCompleted: boolean;
    specimenId?: string;
    blockId?: string | null;
  }>;
  additionalStainTargets?: Array<{
    specimenNumber: string;
    blockNumber: string;
    stainCodes: string[];
    requestedAt: string | null;
  }>;
}

export interface CuttingQueuePageSection {
  items: WorkflowQueueCase[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CuttingQueuePageResponse {
  active: CuttingQueuePageSection;
  blocked: CuttingQueuePageSection;
}

export interface CuttingQueueContext {
  previousCaseId: string | null;
  nextCaseId: string | null;
  otherCaseCount: number;
}

export interface CuttingStatusResult {
  id: string;
  name: string;
}

export interface CuttingCompletionMetadata {
  cuttingCompleted?: true;
  previousStatus?: string;
  currentStatus?: CuttingStatusResult;
}

export interface CuttingCompletionResult extends CuttingCompletionMetadata {
  success: true;
  cuttingCompleted: true;
  previousStatus: string;
  currentStatus: CuttingStatusResult;
}

export interface CuttingScanResult extends CuttingCompletionMetadata {
  slides: any[];
  printJob: { success: true; count: number };
  autoAdvanced: boolean;
}

export interface DistributionQueueCounts {
  pendingStaining: number;
  staining: number;
  awaitingScan: number;
  pendingDistribution: number;
  pendingPathologistReview: number;
  pathologistReview: number;
  pendingShipping: number;
}

export interface DistributionPathologist {
  id: string;
  fullName: string;
  isRemote: boolean;
}

export interface DistributionSlideInfo {
  id: string;
  insufficientTissue?: boolean;
  partLabel: string;
  blockNumber: string;
  slideNumber: string;
  stainCode: string | null;
  stainProcessingKind?: "stain_required" | "unstained";
  requiresConfirmation?: boolean;
  isControlSlide: boolean;
  status: string | null;
  readyForReview: boolean;
  readyForReviewAt: string | null;
}

export interface DistributionClientInfo {
  sendingPractice: { id: string; name: string } | null;
  physician: { id: string; firstName: string; lastName: string } | null;
  surgeryCenterLab: { id: string; name: string } | null;
}

export interface DistributionScanResult {
  case: {
    id: string;
    caseNumber: string;
    externalCaseNumber: string | null;
    patient: { firstName: string; lastName: string } | null;
    pathologistId: string | null;
    currentStatus: { id: string; name: string } | null;
    workflow: {
      id: string;
      accessioningType: string | null;
    } | null;
  } & DistributionClientInfo;
  scanType: "slide" | "block" | "case";
  slidesMarkedReady: number;
  stainingCompleted: boolean;
  allCaseSlides: DistributionSlideInfo[];
  slidesReady: number;
  slidesTotal: number;
  isCaseReady: boolean;
  alreadyReady: boolean;
}

export interface DistributionCase {
  id: string;
  caseNumber: string;
  externalCaseNumber: string | null;
  dateReceived: string;
  isRush: boolean;
  rushReason: string | null;
  isHighPriority: boolean;
  highPriorityReason: string | null;
  isBlocked: boolean;
  patient: {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string | null;
    medicalRecordNumber: string | null;
  } | null;
  pathologist: { id: string; fullName: string; isRemote: boolean } | null;
  procedure: { id: string; name: string } | null;
  workflow: {
    id: string;
    name: string | null;
    accessioningType: string | null;
  } | null;
  facility: { id: string; name: string } | null;
  currentStatus: { id: string; name: string } | null;
  parentCase: { id: string; caseNumber: string } | null;
  sendingPractice: { id: string; name: string } | null;
  physician: { id: string; firstName: string; lastName: string } | null;
  surgeryCenterLab: { id: string; name: string } | null;
  slides: Array<{
    id: string;
    readyForReviewAt: string | null;
    stain?: { processingKind?: "stain_required" | "unstained" } | null;
    requiresConfirmation?: boolean;
  }>;
}

export type DistributionQueueView = "distribution" | "scanner";

export interface DistributionQueueResponse {
  data: DistributionCase[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface DistributionQueueRequest {
  view: DistributionQueueView;
  page: number;
  limit: number;
  filters?: CaseFilters;
}

export interface DistributionConfirmResult {
  success: boolean;
  routed: Array<{
    caseId: string;
    caseNumber: string;
    destination: string;
  }>;
}

export interface DistributionCaseSlidesResult {
  case: {
    id: string;
    caseNumber: string;
    externalCaseNumber: string | null;
    patient: { firstName: string; lastName: string } | null;
    pathologistId: string | null;
    currentStatus: { id: string; name: string } | null;
    workflow: {
      id: string;
      accessioningType: string | null;
    } | null;
  } & DistributionClientInfo;
  allCaseSlides: DistributionSlideInfo[];
  slidesReady: number;
  slidesTotal: number;
  isCaseReady: boolean;
}

export interface MarkSlideReadyResult {
  slideId: string;
  readyForReviewAt: string;
  stainingCompleted: boolean;
  slidesReady: number;
  slidesTotal: number;
  isCaseReady: boolean;
}

export interface MarkSlideNotReadyResult {
  slideId: string;
  slidesReady: number;
  slidesTotal: number;
  isCaseReady: boolean;
}

export interface PresignedUrlResponse {
  url: string;
  key: string;
}

export interface SpecimenMacroForAccessioning {
  id: string;
  code: string;
  description: string | null;
  sourceId: string | null;
  sourceName: string | null;
  subsiteId: string | null;
  subsiteName: string | null;
  findings: string | null;
  collectionMethod: string | null;
  material: string | null;
}

export interface BatchTemplateForAccessioning {
  id: string;
  name: string;
  specimenCount: number;
  defaultMaterial: string | null;
}

export interface CaseIdentityResult {
  caseId: string;
  caseNumber: string;
  casePrefix: string;
  prefixId: string;
  allocationSource?: "sequence" | "reusable_pool";
}

export interface CanonicalExternalCaseNumberResult extends CaseIdentityResult {
  externalCaseNumber: string | null;
  status: "claimed" | "cleared" | "unchanged";
  previousCaseNumber: string;
  displacedTemporaryDraft: boolean;
  displacedDraft?: {
    caseId: string;
    previousCaseNumber: string;
    newCaseNumber: string;
    staleMaterialTypes: Array<"cassettes" | "slides">;
  };
  staleMaterialTypes: Array<"cassettes" | "slides">;
}

export class CanonicalExternalCaseNumberError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CanonicalExternalCaseNumberError";
  }
}

function translateCanonicalExternalCaseNumberError(error: unknown): Error {
  const status = getApiErrorStatus(error);
  if (status === 400) {
    return new CanonicalExternalCaseNumberError(
      "Use an External Case Number no longer than 10 characters."
    );
  }
  if (status === 409) {
    return new CanonicalExternalCaseNumberError(
      "This case number is already assigned to another case."
    );
  }
  if (status === undefined) {
    return new CanonicalExternalCaseNumberError(
      "The connection was interrupted, so the External Case Number is not verified. Try again."
    );
  }
  return new CanonicalExternalCaseNumberError(
    "The External Case Number could not be verified. Try again."
  );
}

export const accessioningService = {
  getSpecimenMacros: async (): Promise<SpecimenMacroForAccessioning[]> => {
    return api.get<SpecimenMacroForAccessioning[]>(
      "/workflows/accessioning/specimen-macros"
    );
  },

  getBatchTemplates: async (): Promise<BatchTemplateForAccessioning[]> => {
    return api.get<BatchTemplateForAccessioning[]>(
      "/workflows/accessioning/batch-templates"
    );
  },

  createDraftCase: async (facilityId: string): Promise<DraftCaseResult> => {
    return api.post<DraftCaseResult>("/workflows/accessioning/draft", {
      facilityId,
    });
  },

  updateCase: async (caseId: string, data: AccessioningData): Promise<any> => {
    return api.put(`/workflows/accessioning/${caseId}`, data);
  },

  previewSpecimenIdentifiers: async (
    caseId: string,
    specimens: AccessioningData["specimens"],
    expectedIdentifiers: Record<string, string>
  ): Promise<SpecimenIdentifierPreview> => {
    const result = await api.put<{
      specimenIdentifierPreview: SpecimenIdentifierPreview;
    }>(`/workflows/accessioning/${caseId}`, {
      specimens,
      specimenIdentifierEdits: { mode: "preview", expectedIdentifiers },
    });
    return result.specimenIdentifierPreview;
  },

  assignPrefix: async (
    caseId: string,
    prefixCode: string
  ): Promise<CaseIdentityResult> => {
    return api.put(`/workflows/accessioning/${caseId}/prefix`, { prefixCode });
  },

  synchronizeCanonicalExternalCaseNumber: async (
    caseId: string,
    input: { externalCaseNumber: string | null; prefixCode?: string }
  ): Promise<CanonicalExternalCaseNumberResult> => {
    try {
      return await api.put(
        `/workflows/accessioning/${caseId}/canonical-external-case-number`,
        input
      );
    } catch (error) {
      throw translateCanonicalExternalCaseNumberError(error);
    }
  },

  correctCaseNumber: async (
    caseId: string,
    correction: { sequence: number; reason: string }
  ): Promise<CaseIdentityResult> => {
    return api.put(`/workflows/accessioning/${caseId}/case-number`, correction);
  },

  completeAccessioning: async (
    caseId: string,
    data: AccessioningData
  ): Promise<{
    caseId: string;
    caseNumber: string;
    slides: any[];
    mappingsCreated?: {
      physicianMappingCreated: boolean;
      surgeryCenterMappingCreated: boolean;
    };
    effectiveNoPatientBillingAndInsuranceInfoRequired: boolean;
  }> => {
    return api.post(`/workflows/accessioning/${caseId}/complete`, data);
  },

  // S3 Upload
  getPresignedUrl: async (
    fileName: string,
    contentType: string
  ): Promise<PresignedUrlResponse> => {
    // Uses accessioning-specific endpoint (folder determined by backend)
    return api.post<PresignedUrlResponse>(
      "/documents/presigned-url/accessioning",
      {
        fileName,
        contentType,
      }
    );
  },

  // AI Extraction with Matching
  // Backend fetches matching data from DB internally
  // caseNumber is passed for prompt logging purposes
  startExtraction: async (
    s3Keys: string[],
    caseNumber?: string
  ): Promise<{ jobId: string }> => {
    return api.post<{ jobId: string }>("/workflows/accessioning/extract", {
      s3Keys,
      caseNumber,
    });
  },

  getExtractionStatus: async (jobId: string): Promise<ExtractionJob> => {
    return api.get<ExtractionJob>(`/workflows/accessioning/extract/${jobId}`);
  },

  rerunExtractionSteps: async (
    jobId: string,
    steps: string[],
    s3Keys: string[]
  ): Promise<{ queued: boolean }> => {
    return api.post<{ queued: boolean }>(
      "/workflows/accessioning/extract/rerun-steps",
      { jobId, steps, s3Keys }
    );
  },
};

// ==================== GROSSING ====================

/**
 * Block data structure for grossing
 */
export interface GrossingBlock {
  id: string;
  caseId: string;
  specimenId: string;
  number: string;
  isAdditional: boolean;
  createdById?: string;
  createdAt: string;
  createdBy?: {
    id: string;
    fullName: string;
  };
  specimen?: {
    id: string;
    number: string;
  };
}

export interface SpecimenGrossingUpdate {
  gross?: string | null;
  internalGrossCodeId?: string | null;
  sourceId?: string | null;
  subsiteId?: string | null;
  collectionMethod?: string | null;
  findings?: string | null;
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
  tissueDisposition?: string | null;
}

/**
 * Cassette label data for printing
 */
export interface CassetteLabel {
  blockId: string;
  barcode: string;
  caseNumber: string;
  specimenLetter: string;
  blockNumber: string;
  patientName: string; // Format: "LastName, FirstName"
  facilityName: string;
}

export const grossingService = {
  getQueue: async (filters?: CaseFilters): Promise<WorkflowQueueCase[]> => {
    const params = new URLSearchParams();

    if (filters) {
      // Support both statusIds and currentStatusIds (filter component uses currentStatusIds)
      const statusIds = filters.statusIds || filters.currentStatusIds;
      if (statusIds?.length) {
        params.set("statusIds", statusIds.join(","));
      }
      if (filters.statusNames?.length) {
        params.set("statusNames", filters.statusNames.join(","));
      }
      if (filters.facilityIds?.length) {
        params.set("facilityIds", filters.facilityIds.join(","));
      }
      if (filters.pathologistIds?.length) {
        params.set("pathologistIds", filters.pathologistIds.join(","));
      }
      if (filters.workflowIds?.length) {
        params.set("workflowIds", filters.workflowIds.join(","));
      }
      if (filters.procedureIds?.length) {
        params.set("procedureIds", filters.procedureIds.join(","));
      }
      if (filters.patientIds?.length) {
        params.set("patientIds", filters.patientIds.join(","));
      }
      if (filters.dateFrom) {
        params.set("dateFrom", filters.dateFrom);
      }
      if (filters.dateTo) {
        params.set("dateTo", filters.dateTo);
      }
      if (filters.search) {
        params.set("search", filters.search);
      }
      if (filters.isBlocked !== undefined) {
        params.set("isBlocked", String(filters.isBlocked));
      }
      if (filters.isRush !== undefined) {
        params.set("isRush", String(filters.isRush));
      }
      if (filters.isHighPriority !== undefined) {
        params.set("isHighPriority", String(filters.isHighPriority));
      }
    }

    const queryString = params.toString();
    const url = queryString
      ? `/workflows/grossing/queue?${queryString}`
      : "/workflows/grossing/queue";
    return api.get<WorkflowQueueCase[]>(url);
  },

  getCaseForGrossing: async (caseId: string): Promise<any> => {
    return api.get(`/workflows/grossing/${caseId}`);
  },

  startGrossing: async (caseId: string): Promise<{ success: boolean }> => {
    return api.post(`/workflows/grossing/${caseId}/start`);
  },

  updateSpecimenGrossing: async (
    caseId: string,
    specimenId: string,
    data: SpecimenGrossingUpdate
  ): Promise<any> => {
    return api.put(
      `/workflows/grossing/${caseId}/specimens/${specimenId}`,
      data
    );
  },

  completeGrossing: async (caseId: string): Promise<{ success: boolean }> => {
    return api.post(`/workflows/grossing/${caseId}/complete`);
  },

  // Block Management
  getSpecimenBlocks: async (specimenId: string): Promise<GrossingBlock[]> => {
    return api.get<GrossingBlock[]>(
      `/workflows/grossing/specimens/${specimenId}/blocks`
    );
  },

  addBlocks: async (
    caseId: string,
    specimenId: string,
    count = 1
  ): Promise<GrossingBlock[]> => {
    return api.post<GrossingBlock[]>(
      `/workflows/grossing/${caseId}/specimens/${specimenId}/blocks`,
      { count }
    );
  },

  deleteBlock: async (caseId: string, blockId: string): Promise<void> => {
    return api.delete(`/workflows/grossing/${caseId}/blocks/${blockId}`);
  },

  printBlockLabels: async (
    caseId: string,
    blockIds: string[]
  ): Promise<{
    labels: CassetteLabel[];
    printJob: { status: string; count: number };
  }> => {
    return api.post(`/workflows/grossing/${caseId}/blocks/print`, { blockIds });
  },
};

// ==================== EMBEDDING ====================

export const embeddingService = {
  getQueue: async (filters?: CaseFilters): Promise<WorkflowQueueCase[]> => {
    const params = new URLSearchParams();
    // Support both statusIds and currentStatusIds (filter component uses currentStatusIds)
    const statusIds = filters?.statusIds || filters?.currentStatusIds;
    if (statusIds?.length) params.append("statusIds", statusIds.join(","));
    if (filters?.facilityIds?.length)
      params.append("facilityIds", filters.facilityIds.join(","));
    if (filters?.pathologistIds?.length)
      params.append("pathologistIds", filters.pathologistIds.join(","));
    if (filters?.workflowIds?.length)
      params.append("workflowIds", filters.workflowIds.join(","));
    if (filters?.procedureIds?.length)
      params.append("procedureIds", filters.procedureIds.join(","));
    if (filters?.patientIds?.length)
      params.append("patientIds", filters.patientIds.join(","));
    if (filters?.dateFrom) params.append("dateFrom", filters.dateFrom);
    if (filters?.dateTo) params.append("dateTo", filters.dateTo);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.isBlocked !== undefined)
      params.append("isBlocked", String(filters.isBlocked));
    if (filters?.isRush !== undefined)
      params.append("isRush", String(filters.isRush));
    if (filters?.isHighPriority !== undefined)
      params.append("isHighPriority", String(filters.isHighPriority));

    const queryString = params.toString();
    const url = queryString
      ? `/workflows/embedding/queue?${queryString}`
      : "/workflows/embedding/queue";
    return api.get<WorkflowQueueCase[]>(url);
  },

  getCaseForEmbedding: async (caseId: string): Promise<any> => {
    return api.get(`/workflows/embedding/${caseId}`);
  },

  startEmbedding: async (caseId: string): Promise<{ success: boolean }> => {
    return api.post(`/workflows/embedding/${caseId}/start`);
  },

  completeEmbedding: async (
    caseId: string
  ): Promise<{ success: boolean; embeddingCompleted: boolean }> => {
    return api.post(`/workflows/embedding/${caseId}/complete`);
  },

  markBlockAsEmbedded: async (
    caseId: string,
    specimenNumber: string,
    blockNumber: string
  ): Promise<{
    success: boolean;
    embeddingCompleted: boolean;
    block: any;
  }> => {
    return api.post(`/workflows/embedding/${caseId}/blocks/scan`, {
      specimenNumber,
      blockNumber,
    });
  },
};

// ==================== CUTTING ====================

function appendWorkflowQueueFilters(
  params: URLSearchParams,
  filters?: CaseFilters
): void {
  if (!filters) return;
  const { currentStatusIds, statusIds, ...remainingFilters } = filters;
  const normalizedStatusIds = statusIds || currentStatusIds;
  if (normalizedStatusIds?.length) {
    params.set("statusIds", normalizedStatusIds.join(","));
  }
  appendCaseFilters(params, remainingFilters);
}

export interface CuttingQueuePageRequest {
  activePage: number;
  blockedPage: number;
  limit: number;
  filters?: CaseFilters;
  sort?: "received-asc" | "case-asc" | "case-desc";
}

export const cuttingService = {
  getQueue: async (filters?: CaseFilters): Promise<WorkflowQueueCase[]> => {
    const params = new URLSearchParams();
    // Support both statusIds and currentStatusIds (filter component uses currentStatusIds)
    const statusIds = filters?.statusIds || filters?.currentStatusIds;
    if (statusIds?.length) params.append("statusIds", statusIds.join(","));
    if (filters?.facilityIds?.length)
      params.append("facilityIds", filters.facilityIds.join(","));
    if (filters?.pathologistIds?.length)
      params.append("pathologistIds", filters.pathologistIds.join(","));
    if (filters?.workflowIds?.length)
      params.append("workflowIds", filters.workflowIds.join(","));
    if (filters?.procedureIds?.length)
      params.append("procedureIds", filters.procedureIds.join(","));
    if (filters?.patientIds?.length)
      params.append("patientIds", filters.patientIds.join(","));
    if (filters?.dateFrom) params.append("dateFrom", filters.dateFrom);
    if (filters?.dateTo) params.append("dateTo", filters.dateTo);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.isBlocked !== undefined)
      params.append("isBlocked", String(filters.isBlocked));
    if (filters?.isRush !== undefined)
      params.append("isRush", String(filters.isRush));
    if (filters?.isHighPriority !== undefined)
      params.append("isHighPriority", String(filters.isHighPriority));

    const queryString = params.toString();
    const url = queryString
      ? `/workflows/cutting/queue?${queryString}`
      : "/workflows/cutting/queue";
    return api.get<WorkflowQueueCase[]>(url);
  },

  getQueuePage: async ({
    activePage,
    blockedPage,
    limit,
    filters,
    sort,
  }: CuttingQueuePageRequest): Promise<CuttingQueuePageResponse> => {
    const params = new URLSearchParams({
      activePage: String(activePage),
      blockedPage: String(blockedPage),
      limit: String(limit),
    });
    appendWorkflowQueueFilters(params, filters);
    if (sort && sort !== "received-asc") params.set("sort", sort);
    return api.get<CuttingQueuePageResponse>(
      `/workflows/cutting/queue-page?${params.toString()}`
    );
  },

  exportAdditionalStains: async (filters?: CaseFilters): Promise<void> => {
    const params = new URLSearchParams();
    appendWorkflowQueueFilters(params, filters);
    const response = await apiClient.get(
      `/workflows/cutting/additional-stains/export?${params.toString()}`,
      { responseType: "blob", timeout: 120_000 }
    );
    triggerBlobDownload(
      response.data as Blob,
      getResponseDownloadFilename(
        response.headers["content-disposition"],
        "additional-stains.xlsx"
      )
    );
  },

  getQueueContext: async (caseId: string): Promise<CuttingQueueContext> => {
    return api.get<CuttingQueueContext>(
      `/workflows/cutting/${caseId}/queue-context`
    );
  },

  getCaseForCutting: async (caseId: string): Promise<any> => {
    return api.get(`/workflows/cutting/${caseId}`);
  },

  startCutting: async (caseId: string): Promise<{ success: boolean }> => {
    return api.post(`/workflows/cutting/${caseId}/start`);
  },

  processCassetteScan: async (
    caseId: string,
    blockId: string
  ): Promise<CuttingScanResult> => {
    return api.post(`/workflows/cutting/${caseId}/scan`, { blockId });
  },

  reprintSlides: async (
    slideIds: string[]
  ): Promise<{ success: boolean; count: number }> => {
    return api.post("/workflows/cutting/reprint", { slideIds });
  },

  completeCutting: async (caseId: string): Promise<CuttingCompletionResult> => {
    return api.post(`/workflows/cutting/${caseId}/complete`);
  },

  cancelCutting: async (caseId: string): Promise<{ success: boolean }> => {
    return api.post(`/workflows/cutting/${caseId}/cancel`);
  },
};

// ==================== STAINING ====================

export interface StainingDistributionStats {
  distributed24h: number;
  failed: number;
  pending: number;
}

export interface VendorProgress {
  vendorId: string;
  vendorName: string;
  minimum: number;
  sent: number;
  completed: number;
}

// Per-category queue/completed counts returned by the staining slides endpoint.
export interface StainingCategoryCount {
  queue: number;
  completed: number;
}

export interface StainingCounts {
  all: StainingCategoryCount;
  ihc: StainingCategoryCount;
  nonIhc: StainingCategoryCount;
}

export type StainingCategory = "all" | "ihc" | "non-ihc";
export type StainingView = "queue" | "completed";

export interface StainingSlidesResponse {
  data: any[];
  counts: StainingCounts;
}

export interface ManualIHCStainingCompletionResult {
  slideId: string;
  status: "staining_completed";
  stainingCompletedAt: string;
  alreadyProcessed: boolean;
  caseAdvanced: boolean;
}

export const BIOCARE_PREFLIGHT_SUMMARY =
  "Biocare orders not sent: correct the highlighted physician information, then retry.";

export type BiocarePreflightReason =
  | "BIOCARE_PHYSICIAN_MISSING"
  | "BIOCARE_PHYSICIAN_NPI_MISSING";

export interface BiocarePreflightRowError {
  slideId: string;
  caseId: string;
  code: BiocarePreflightReason;
  message: string;
}

export interface BiocarePreflightFailure {
  code: "BIOCARE_PREFLIGHT_FAILED";
  message: string;
  errors: BiocarePreflightRowError[];
}

export const getBiocarePreflightFailure = (
  error: unknown
): BiocarePreflightFailure | undefined => {
  const response = (error as any)?.response;
  if (response?.status !== 422) return undefined;
  const data = response.data;
  if (
    data?.code !== "BIOCARE_PREFLIGHT_FAILED" ||
    !Array.isArray(data.errors)
  ) {
    return undefined;
  }
  return data as BiocarePreflightFailure;
};

export interface StainingSlidesParams {
  category: StainingCategory;
  view: StainingView;
  page: number;
  limit: number;
  filters?: CaseFilters;
}

// Appends the shared case-level filter params used by every staining query.
function appendStainingCaseFilters(
  params: URLSearchParams,
  filters?: CaseFilters
): void {
  if (filters?.facilityIds?.length)
    params.append("facilityIds", filters.facilityIds.join(","));
  if (filters?.pathologistIds?.length)
    params.append("pathologistIds", filters.pathologistIds.join(","));
  if (filters?.workflowIds?.length)
    params.append("workflowIds", filters.workflowIds.join(","));
  if (filters?.procedureIds?.length)
    params.append("procedureIds", filters.procedureIds.join(","));
  if (filters?.patientIds?.length)
    params.append("patientIds", filters.patientIds.join(","));
  if (filters?.dateFrom) params.append("dateFrom", filters.dateFrom);
  if (filters?.dateTo) params.append("dateTo", filters.dateTo);
  if (filters?.search) params.append("search", filters.search);
  if (filters?.isRush !== undefined)
    params.append("isRush", String(filters.isRush));
  if (filters?.isHighPriority !== undefined)
    params.append("isHighPriority", String(filters.isHighPriority));
}

// Helper to build staining filter params (legacy queue endpoint).
function buildStainingFilterParams(
  includeCompleted: boolean,
  filters?: CaseFilters
): string {
  const params = new URLSearchParams();
  if (includeCompleted) params.append("includeCompleted", "true");
  appendStainingCaseFilters(params, filters);
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

export const stainingService = {
  getQueue: async (
    includeCompleted = false,
    filters?: CaseFilters
  ): Promise<any[]> => {
    const params = buildStainingFilterParams(includeCompleted, filters);
    return api.get<any[]>(`/workflows/staining/queue${params}`);
  },

  // Single paginated endpoint: returns the current page of rows for
  // (category, view, page) plus queue/completed counts for every tab.
  getStainingSlides: async ({
    category,
    view,
    page,
    limit,
    filters,
  }: StainingSlidesParams): Promise<StainingSlidesResponse> => {
    const params = new URLSearchParams();
    params.append("category", category);
    params.append("view", view);
    params.append("page", String(page));
    params.append("limit", String(limit));
    appendStainingCaseFilters(params, filters);
    return api.get<StainingSlidesResponse>(
      `/workflows/staining/slides?${params.toString()}`
    );
  },

  getDistributionStats: async (): Promise<StainingDistributionStats> => {
    return api.get<StainingDistributionStats>("/workflows/staining/stats");
  },

  getVendorProgress: async (): Promise<VendorProgress[]> => {
    return api.get<VendorProgress[]>("/workflows/staining/vendor-progress");
  },

  // Distribute every pending IHC slide matching the active filters. The
  // backend resolves the slide set server-side so distribution covers the
  // whole queue, not just the page the user is currently viewing.
  distributeIHCSlides: async (
    filters?: CaseFilters
  ): Promise<{
    distributed: number;
    failed: number;
  }> => {
    return api.post("/workflows/staining/distribute", {
      distributeAll: true,
      filters,
    });
  },

  processNow: async (): Promise<{
    processed: number;
    distributed: number;
    failed: number;
  }> => {
    return api.post("/workflows/staining/process-now", {});
  },

  retryFailed: async (): Promise<{ retried: number }> => {
    return api.post("/workflows/staining/retry-failed", {});
  },

  // Non-IHC staining completion
  completeNonIHCStainingScan: async (
    barcode: string
  ): Promise<{
    slide: any;
    alreadyCompleted: boolean;
    caseReady: boolean;
  }> => {
    return api.post("/workflows/staining/non-ihc/scan", { barcode });
  },

  bulkCompleteNonIHCStaining: async (
    slideIds: string[]
  ): Promise<{
    completed: number;
    alreadyCompleted: number;
    failed: number;
    errors: Array<{ slideId: string; error: string }>;
    casesReady: string[];
  }> => {
    return api.post("/workflows/staining/non-ihc/bulk-complete", { slideIds });
  },

  manualCompleteIHCStaining: async (
    slideId: string,
    notes?: string
  ): Promise<ManualIHCStainingCompletionResult> => {
    return api.post<ManualIHCStainingCompletionResult>(
      `/workflows/slides/${slideId}/manual-complete`,
      notes?.trim() ? { notes: notes.trim() } : {}
    );
  },

  reassignSlideMachine: async (
    slideId: string,
    machineId: string | null
  ): Promise<{ slideId: string; machineId: string | null }> => {
    return api.put(`/workflows/slides/${slideId}/machine`, { machineId });
  },

  setSlideStainerType: async (
    slideId: string,
    stainerType: "biocare" | "leica" | null
  ): Promise<{ slideId: string; stainerType: "biocare" | "leica" | null }> => {
    return api.put(`/workflows/slides/${slideId}/stainer-type`, {
      stainerType,
    });
  },

  resendIhcSlide: async (slideId: string): Promise<any> => {
    return api.post(`/workflows/staining/slides/${slideId}/resend`, {});
  },
};

// ==================== DISTRIBUTION ====================

export const distributionService = {
  getPendingCases: async (): Promise<DistributionCase[]> => {
    return api.get<DistributionCase[]>("/workflows/distribution/pending");
  },

  getQueue: async ({
    view,
    page,
    limit,
    filters,
  }: DistributionQueueRequest): Promise<DistributionQueueResponse> => {
    const params = new URLSearchParams({
      view,
      page: String(page),
      limit: String(limit),
    });
    appendWorkflowQueueFilters(params, filters);
    return api.get<DistributionQueueResponse>(
      `/workflows/distribution/queue?${params.toString()}`
    );
  },

  getQueueCounts: async (): Promise<DistributionQueueCounts> => {
    return api.get<DistributionQueueCounts>("/workflows/distribution/counts");
  },

  getPathologists: async (): Promise<DistributionPathologist[]> => {
    return api.get<DistributionPathologist[]>(
      "/workflows/distribution/pathologists"
    );
  },

  processScan: async (
    barcode: string,
    expectedCaseId?: string
  ): Promise<DistributionScanResult> => {
    return api.post<DistributionScanResult>("/workflows/distribution/scan", {
      barcode,
      ...(expectedCaseId ? { expectedCaseId } : {}),
    });
  },

  assignPathologist: async (
    caseId: string,
    pathologistId: string | null
  ): Promise<{ success: boolean }> => {
    return api.put(`/workflows/distribution/${caseId}/pathologist`, {
      pathologistId,
    });
  },

  confirmDistribution: async (
    caseIds: string[]
  ): Promise<DistributionConfirmResult> => {
    return api.post<DistributionConfirmResult>(
      "/workflows/distribution/confirm",
      { caseIds }
    );
  },

  getCaseSlides: async (
    caseId: string
  ): Promise<DistributionCaseSlidesResult> => {
    return api.get<DistributionCaseSlidesResult>(
      `/workflows/distribution/${caseId}/slides`
    );
  },

  markSlideReady: async (slideId: string): Promise<MarkSlideReadyResult> => {
    return api.put<MarkSlideReadyResult>(
      `/workflows/distribution/slides/${slideId}/mark-ready`
    );
  },

  markSlideNotReady: async (
    slideId: string
  ): Promise<MarkSlideNotReadyResult> => {
    return api.put<MarkSlideNotReadyResult>(
      `/workflows/distribution/slides/${slideId}/mark-not-ready`
    );
  },
};

// ==================== SCANNER ====================

export const scannerService = {
  confirmScan: async (
    caseId: string
  ): Promise<{ success: boolean; newStatus: string }> => {
    return api.post(`/workflows/cases/${caseId}/confirm-scan`);
  },
};

// ==================== PATHOLOGIST REVIEW ====================

export type PathologistReviewQueueScope = "assigned" | "all";

export interface DiagnosisCategory {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  markerShape: string | null;
  markerColor: string | null;
}

export interface PathologistReviewQueueFilters {
  queueScope?: PathologistReviewQueueScope;
  partition?: "waiting";
  statusIds?: string[];
  facilityIds?: string[];
  workflowIds?: string[];
  procedureIds?: string[];
  patientIds?: string[];
  pathologistIds?: string[];
  physicianId?: string;
  flagDefinitionIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  dateCollected?: string;
  search?: string;
  isBlocked?: boolean;
  isRush?: boolean;
  isHighPriority?: boolean;
  olderThanDays?: number;
  prefixKeys?: string[];
  caseType?: string;
  tatMinDays?: number;
  tatOverdue?: boolean;
  hasPendingTests?: boolean;
  overdueThresholdDays?: number;
  summaryFilter?: string;
  sorts?: QueueServerSort[];
  page?: number;
  pageSize?: number;
}

export interface QueueServerSort {
  column: string;
  direction: "asc" | "desc";
}

export interface PathologistQueuePage<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface PathologistIncomingQueueFilters {
  pathologistId?: string;
  prefixKeys?: string[];
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  isRush?: boolean;
  isHighPriority?: boolean;
  stage?: string;
  currentStatusIds?: string[];
  facilityIds?: string[];
  sorts?: QueueServerSort[];
}

export interface PathologistCompletedQueueFilters {
  prefixKeys?: string[];
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  isRush?: boolean;
  isHighPriority?: boolean;
  caseType?: string;
  facilityIds?: string[];
  sorts?: QueueServerSort[];
}

export interface PathologistReviewQueueCase {
  id: string;
  caseNumber: string;
  externalCaseNumber?: string;
  dateReceived: string;
  isBlocked?: boolean;
  isRush?: boolean;
  isHighPriority?: boolean;
  patient?: {
    firstName: string;
    lastName: string;
    dateOfBirth?: string;
    medicalRecordNumber?: string;
  };
  pathologist?: {
    id: string;
    fullName: string;
  };
  physician?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  sendingPractice?: {
    id: string;
    name: string;
  };
  dateCollected?: string | null;
  currentStatus?: {
    id: string;
    name: string;
  };
  workflow?: {
    id: string;
    name?: string;
    accessioningType?: string;
  };
  facility?: {
    id: string;
    name: string;
  };
  procedure?: {
    id: string;
    name: string;
  };
  specimenCount?: number;
  slideCount?: number;
  lockedBy?: string;
  lockedByName?: string;
  pendingAdditionalStainsCount?: number;
  additionalStainSlideCount?: number;
  isPartiallyFinalized?: boolean;
  hasOpenDiscussion?: boolean;
  discussionCategory?: string | null;
  hasPendingQaReview?: boolean;
  qaReviewInstitution?: string | null;
  hasUnsignedAddendum?: boolean;
  hasSentOut?: boolean;
  sendOutReviewStatus?: "pending_shipping" | "sent_out" | "report_ready" | null;
  sendOutShippedAt?: string | null;
  tatDays?: number;
  isOverdue?: boolean;
  step?: string;
  caseType?: string | null;
  signedOffAt?: string | null;
  pathologistAssignmentRevision?: number;
  reviewAction?: "tc_completion" | null;
}

export interface PathologistReviewStats {
  total: number;
  new: number;
  pendingStains: number;
  discussion: number;
  qaReview: number;
  // Venn diagram segments (3-circle: Rush, High Priority, Overdue)
  rushOnly: number;
  priorityOnly: number;
  overdueOnly: number;
  rushAndPriority: number;
  rushAndOverdue: number;
  priorityAndOverdue: number;
  allThree: number;
  malignancy: number;
  distributed: number;
  tatAvgDays: number;
}

export interface AdditionalStainNoReportChangeResult {
  success: true;
  alreadyAcknowledged: boolean;
  acknowledgedAt: string;
  restoredStatus: "Signed Off";
}

export interface IncomingLabCase {
  id: string;
  caseNumber: string;
  externalCaseNumber?: string;
  dateReceived: string;
  dateCollected?: string;
  accessioningType?: string;
  procedureName?: string;
  specimenCount?: number;
  blockCount?: number;
  mrn?: string;
  patient?: {
    firstName: string;
    lastName: string;
    dateOfBirth?: string;
  };
  physician?: {
    id: string;
    fullName: string;
  };
  facility?: {
    id: string;
    name: string;
  };
  sendingPractice?: {
    id: string;
    name: string;
  };
  currentStep: string;
  isRush?: boolean;
  isHighPriority?: boolean;
}

export interface IncomingLabQueueResponse {
  cases: IncomingLabCase[];
  counts: {
    grossing: number;
    embedding: number;
    cutting: number;
    staining: number;
    awaitingSendOut: number;
    pendingStains: number;
    total: number;
  };
}

export interface IncomingLabQueuePage
  extends PathologistQueuePage<IncomingLabCase> {
  counts: IncomingLabQueueResponse["counts"];
  prefixOptions: string[];
}

function appendPagedQueueParams(
  params: URLSearchParams,
  page: number,
  pageSize: number,
  sorts?: QueueServerSort[]
) {
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  if (sorts?.length) {
    params.set("sorts", JSON.stringify(sorts));
  }
}

function appendSharedQueueFilters(
  params: URLSearchParams,
  filters: PathologistIncomingQueueFilters | PathologistCompletedQueueFilters
) {
  if (filters.prefixKeys?.length)
    params.set("prefixKeys", filters.prefixKeys.join(","));
  if (filters.search?.trim()) params.set("search", filters.search.trim());
  if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.set("dateTo", filters.dateTo);
  if (filters.isRush !== undefined)
    params.set("isRush", String(filters.isRush));
  if (filters.isHighPriority !== undefined)
    params.set("isHighPriority", String(filters.isHighPriority));
  if (filters.facilityIds?.length)
    params.set("facilityIds", filters.facilityIds.join(","));
}

function buildReviewQueueUrl(filters?: PathologistReviewQueueFilters): string {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.queueScope) {
      params.set("queueScope", filters.queueScope);
    }
    if (filters.partition) {
      params.set("partition", filters.partition);
    }
    // Support both statusIds and currentStatusIds (filter component uses currentStatusIds)
    const statusIds = filters.statusIds || (filters as any).currentStatusIds;
    if (statusIds?.length) {
      params.set("statusIds", statusIds.join(","));
    }
    if (filters.facilityIds?.length) {
      params.set("facilityIds", filters.facilityIds.join(","));
    }
    if (filters.workflowIds?.length) {
      params.set("workflowIds", filters.workflowIds.join(","));
    }
    if (filters.procedureIds?.length) {
      params.set("procedureIds", filters.procedureIds.join(","));
    }
    if (filters.patientIds?.length) {
      params.set("patientIds", filters.patientIds.join(","));
    }
    if (filters.pathologistIds?.length) {
      params.set("pathologistIds", filters.pathologistIds.join(","));
    }
    if (filters.physicianId) {
      params.set("physicianId", filters.physicianId);
    }
    if (filters.flagDefinitionIds?.length) {
      params.set("flagDefinitionIds", filters.flagDefinitionIds.join(","));
    }
    if (filters.dateFrom) {
      params.set("dateFrom", filters.dateFrom);
    }
    if (filters.dateTo) {
      params.set("dateTo", filters.dateTo);
    }
    if (filters.dateCollected) {
      params.set("dateCollected", filters.dateCollected);
    }
    if (filters.search) {
      params.set("search", filters.search);
    }
    if (filters.isBlocked !== undefined) {
      params.set("isBlocked", String(filters.isBlocked));
    }
    if (filters.isRush !== undefined) {
      params.set("isRush", String(filters.isRush));
    }
    if (filters.isHighPriority !== undefined) {
      params.set("isHighPriority", String(filters.isHighPriority));
    }
    if (filters.olderThanDays !== undefined) {
      params.set("olderThanDays", String(filters.olderThanDays));
    }
    if (filters.prefixKeys?.length) {
      params.set("prefixKeys", filters.prefixKeys.join(","));
    }
    if (filters.caseType) params.set("caseType", filters.caseType);
    if (filters.tatMinDays !== undefined)
      params.set("tatMinDays", String(filters.tatMinDays));
    if (filters.tatOverdue !== undefined)
      params.set("tatOverdue", String(filters.tatOverdue));
    if (filters.overdueThresholdDays !== undefined)
      params.set("overdueThresholdDays", String(filters.overdueThresholdDays));
    if (filters.hasPendingTests !== undefined)
      params.set("hasPendingTests", String(filters.hasPendingTests));
    if (filters.summaryFilter)
      params.set("summaryFilter", filters.summaryFilter);
    if (filters.page !== undefined && filters.pageSize !== undefined)
      appendPagedQueueParams(
        params,
        filters.page,
        filters.pageSize,
        filters.sorts
      );
  }

  const queryString = params.toString();
  const url = queryString
    ? `/workflows/pathologist-review/queue?${queryString}`
    : "/workflows/pathologist-review/queue";
  return url;
}

export const pathologistReviewService = {
  getPrefixOptions: async (
    options: Pick<
      PathologistReviewQueueFilters,
      "queueScope" | "partition"
    > = {}
  ): Promise<string[]> => {
    if (!options.queueScope && !options.partition) {
      return api.get<string[]>("/workflows/pathologist-review/prefix-options");
    }
    return api.get<string[]>("/workflows/pathologist-review/prefix-options", {
      params: options,
    });
  },

  getQueue: async (
    filters?: PathologistReviewQueueFilters
  ): Promise<PathologistReviewQueueCase[]> => {
    return api.get<PathologistReviewQueueCase[]>(buildReviewQueueUrl(filters));
  },

  getPagedQueue: async (
    filters: PathologistReviewQueueFilters,
    page: number,
    pageSize = 20
  ): Promise<PathologistQueuePage<PathologistReviewQueueCase>> => {
    return api.get<PathologistQueuePage<PathologistReviewQueueCase>>(
      buildReviewQueueUrl({ ...filters, page, pageSize })
    );
  },

  getCompletedCases: async (): Promise<PathologistReviewQueueCase[]> => {
    // Scoped server-side to the authenticated pathologist (their signed-off cases)
    return api.get<PathologistReviewQueueCase[]>(
      "/workflows/pathologist-review/completed"
    );
  },

  getPagedCompletedCases: async (
    filters: PathologistCompletedQueueFilters,
    page: number,
    pageSize = 20
  ): Promise<
    PathologistQueuePage<PathologistReviewQueueCase> & {
      prefixOptions: string[];
    }
  > => {
    const params = new URLSearchParams();
    appendSharedQueueFilters(params, filters);
    if (filters.caseType) params.set("caseType", filters.caseType);
    appendPagedQueueParams(params, page, pageSize, filters.sorts);
    return api.get(`/workflows/pathologist-review/completed?${params}`);
  },

  getStats: async (pathologistId?: string): Promise<PathologistReviewStats> => {
    const params = new URLSearchParams();
    if (pathologistId) {
      params.set("pathologistId", pathologistId);
    }
    const queryString = params.toString();
    const url = queryString
      ? `/workflows/pathologist-review/stats?${queryString}`
      : "/workflows/pathologist-review/stats";
    return api.get<PathologistReviewStats>(url);
  },

  getCaseForPathologistReview: async (caseId: string): Promise<any> => {
    return api.get(`/workflows/pathologist-review/${caseId}`);
  },

  updateCaseRequisition: async (
    caseId: string,
    data: {
      patient?: Record<string, any>;
      case?: Record<string, any>;
    }
  ): Promise<{ message: string }> => {
    return api.patch(
      `/workflows/pathologist-review/${caseId}/requisition`,
      data
    );
  },

  startPathologistReview: async (
    caseId: string
  ): Promise<{ success: boolean }> => {
    return api.post(`/workflows/pathologist-review/${caseId}/start`);
  },

  acknowledgeAdditionalStainNoReportChange: async (
    caseId: string,
    input: {
      eventId: string;
      expectedAssignmentRevision: number;
      requestId: string;
    }
  ): Promise<AdditionalStainNoReportChangeResult> => {
    return api.post(
      `/workflows/pathologist-review/${caseId}/no-report-change`,
      input
    );
  },

  completeTcReview: async (
    caseId: string,
    expectedAssignmentRevision: number
  ): Promise<{
    success: true;
    completedAt: string;
    alreadyCompleted: boolean;
  }> => {
    return api.post(`/workflows/pathologist-review/${caseId}/tc-complete`, {
      expectedAssignmentRevision,
    });
  },

  completeTcExternalAction: async (
    caseId: string,
    expectedAssignmentRevision: number
  ): Promise<{
    success: true;
    completedAt: string;
    alreadyCompleted: boolean;
  }> => {
    return api.post(`/workflows/cases/${caseId}/tc-external-action-complete`, {
      expectedAssignmentRevision,
    });
  },

  getDiagnosisMacros: async (): Promise<
    Array<{
      id: string;
      code: string;
      label: string;
      diagnosisText: string;
      microscopicDescription: string | null;
      category: string | null;
      cptCode: string | null;
      icdCode: string | null;
      icdCodes: string[];
      scope: "global" | "personal";
      createdById: string | null;
    }>
  > => {
    return api.get("/workflows/pathologist-review/diagnosis-macros");
  },

  getDiagnosisCategories: async (): Promise<DiagnosisCategory[]> => {
    const categories = await api.get<DiagnosisCategory[]>(
      "/workflows/pathologist-review/diagnosis-categories"
    );

    return deduplicateDiagnosisCategoriesByName(categories);
  },

  saveDiagnosisDraft: async (
    caseId: string,
    diagnoses: Array<{
      specimenId: string;
      macroCode?: string | null;
      diagnosisText?: string;
      microscopicDescription?: string;
      location?: string;
      category?: string;
      cptCode?: string;
      icdCode?: string | null;
      frozenInterpretation?: string;
      color?: string;
      sizeCm?: string;
      sizeMm?: string;
      consistency?: string;
    }>,
    grossing?: Array<{
      specimenId: string;
      grossCodeId?: string;
      grossDescription?: string;
      sourceId?: string | null;
      subsiteId?: string | null;
      collectionMethod?: string | null;
      findings?: string | null;
      material?: string | null;
      fragmentCount?: number | null;
      fragmentCountQualifier?: FragmentCountQualifier | null;
    }>,
    caseLevelData?: { frozenInterpretation?: string }
  ): Promise<{ success: boolean; savedCount: number }> => {
    return api.post(`/workflows/pathologist-review/${caseId}/save-draft`, {
      diagnoses,
      grossing,
      caseLevelData,
    });
  },

  // Add a specimen during review (creates specimen + block + H&E slide).
  addSpecimen: async (
    caseId: string,
    data: {
      sourceId?: string | null;
      subsiteId?: string | null;
      material?: string | null;
      collectionMethod?: string | null;
      gross?: string | null;
      comments?: string | null;
    }
  ): Promise<{ id: string; number: string }> => {
    return api.post(`/workflows/pathologist-review/${caseId}/specimens`, data);
  },

  // Soft-archive (remove) a specimen — recoverable, non-destructive.
  archiveSpecimen: async (
    caseId: string,
    specimenId: string
  ): Promise<{ message: string }> => {
    return api.post(
      `/workflows/pathologist-review/${caseId}/specimens/${specimenId}/archive`
    );
  },

  // Recover a previously archived specimen.
  recoverSpecimen: async (
    caseId: string,
    specimenId: string
  ): Promise<{ id: string; number: string }> => {
    return api.post(
      `/workflows/pathologist-review/${caseId}/specimens/${specimenId}/restore`
    );
  },

  /**
   * Get presigned upload URL for pathology review documents
   */
  getPresignedUrl: async (
    fileName: string,
    contentType: string
  ): Promise<{ url: string; key: string }> => {
    return api.post<{ url: string; key: string }>(
      "/documents/presigned-url/pathology-review",
      { fileName, contentType }
    );
  },

  getIncomingQueue: async (
    pathologistId?: string
  ): Promise<IncomingLabQueueResponse> => {
    const query = pathologistId ? `?pathologistId=${pathologistId}` : "";
    return api.get<IncomingLabQueueResponse>(
      `/workflows/pathologist-review/incoming${query}`
    );
  },

  getPagedIncomingQueue: async (
    filters: PathologistIncomingQueueFilters,
    page: number,
    pageSize = 20
  ): Promise<IncomingLabQueuePage> => {
    const params = new URLSearchParams();
    appendSharedQueueFilters(params, filters);
    if (filters.pathologistId)
      params.set("pathologistId", filters.pathologistId);
    if (filters.stage) params.set("stage", filters.stage);
    if (filters.currentStatusIds?.length)
      params.set("currentStatusIds", filters.currentStatusIds.join(","));
    appendPagedQueueParams(params, page, pageSize, filters.sorts);
    return api.get(`/workflows/pathologist-review/incoming?${params}`);
  },

  // ---- Pathologist Comments ----

  getPathologistComments: async (
    caseId: string
  ): Promise<PathologistComment[]> => {
    return api.get<PathologistComment[]>(
      `/workflows/pathologist-review/${caseId}/pathologist-comments`
    );
  },

  addPathologistComment: async (
    caseId: string,
    commentText: string
  ): Promise<PathologistComment> => {
    return api.post<PathologistComment>(
      `/workflows/pathologist-review/${caseId}/pathologist-comments`,
      { commentText }
    );
  },

  deletePathologistComment: async (
    caseId: string,
    commentId: string
  ): Promise<void> => {
    return api.delete(
      `/workflows/pathologist-review/${caseId}/pathologist-comments/${commentId}`
    );
  },
};

export interface PathologistComment {
  id: string;
  commentText: string;
  createdAt: string;
  createdBy: {
    id: string;
    fullName: string;
    credentials: string | null;
  } | null;
}

// ==================== QA REVIEW QUEUE ====================

export interface QaReviewQueueFilters {
  reviewerId?: string;
  institutionIds?: string[];
  search?: string;
}

export interface QaReviewQueueItem {
  id: string;
  caseId: string;
  caseNumber: string;
  sendOutId: string;
  institutionId: string | null;
  institutionName: string;
  reviewerId: string;
  reviewerName: string;
  originalPathologistId: string | null;
  originalPathologistName: string;
  status: string;
  agreementLevel: string | null;
  recommendedAction: string | null;
  createdAt: string;
  daysPending: number;
  patientName: string;
  siteName: string;
  specimenDescription: string;
  externalReportReceivedAt: string | null;
}

export interface QaReviewQueueStats {
  total: number;
  byInstitution: {
    institutionId: string;
    institutionName: string;
    count: number;
  }[];
}

export const qaReviewService = {
  getQueue: async (
    filters?: QaReviewQueueFilters
  ): Promise<QaReviewQueueItem[]> => {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.reviewerId) {
        params.set("reviewerId", filters.reviewerId);
      }
      if (filters.institutionIds?.length) {
        params.set("institutionIds", filters.institutionIds.join(","));
      }
      if (filters.search) {
        params.set("search", filters.search);
      }
    }

    const queryString = params.toString();
    const url = queryString
      ? `/workflows/qa-review/queue?${queryString}`
      : "/workflows/qa-review/queue";
    return api.get<QaReviewQueueItem[]>(url);
  },

  getStats: async (reviewerId?: string): Promise<QaReviewQueueStats> => {
    const params = new URLSearchParams();
    if (reviewerId) {
      params.set("reviewerId", reviewerId);
    }
    const queryString = params.toString();
    const url = queryString
      ? `/workflows/qa-review/stats?${queryString}`
      : "/workflows/qa-review/stats";
    return api.get<QaReviewQueueStats>(url);
  },
};

// ==================== QA REVIEW DETAIL (for QA Comparison Form) ====================

export type QaAgreementLevel =
  | "complete"
  | "minor_variation"
  | "partial"
  | "discrepancy"
  | "major_discrepancy";

export type QaAction =
  | "none"
  | "notify_pathologist"
  | "internal_discussion"
  | "amendment_needed";

export interface QaReviewSlide {
  id: string;
  number: string;
  stainCode: string;
  stainDescription: string;
}

export interface QaReviewBlock {
  id: string;
  number: string;
  slides: QaReviewSlide[];
}

export interface QaReviewDiagnosis {
  specimenId: string;
  specimenNumber: string;
  siteName: string;
  diagnosisText: string;
  microscopicDescription: string | null;
  blocks: QaReviewBlock[];
}

export interface QaReviewDetail {
  qaReview: {
    id: string;
    caseId: string | null;
    sendOutId: string;
    originalPathologistId: string | null;
    reviewedById: string;
    status: string;
    agreementLevel: QaAgreementLevel | null;
    recommendedAction: QaAction;
    internalDiagnosisSummary: string | null;
    externalDiagnosisSummary: string | null;
    discrepancies: string | null;
    comments: string | null;
    actionTaken: string | null;
    actionTakenById: string | null;
    actionTakenAt: string | null;
    reviewedAt: string | null;
    completedAt: string | null;
    createdAt: string;
    updatedAt: string;
  };
  caseNumber: string;
  patient: {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string | null;
  };
  institutionName: string;
  originalPathologistName: string;
  reviewerName: string;
  gcpDiagnoses: QaReviewDiagnosis[];
  externalDiagnosis: string | null;
  externalReportDate: string | null;
  externalReportFileKey: string | null;
}

export interface QaReviewUpdateData {
  agreementLevel?: QaAgreementLevel | null;
  recommendedAction?: QaAction;
  comments?: string;
  internalDiagnosisSummary?: string;
  externalDiagnosisSummary?: string;
  discrepancies?: string;
}

export interface QaReviewCompleteData {
  agreementLevel: QaAgreementLevel;
  recommendedAction: QaAction;
  comments?: string;
  internalDiagnosisSummary?: string;
  externalDiagnosisSummary?: string;
  discrepancies?: string;
}

export const qaReviewDetailService = {
  /**
   * Get a single QA review by ID with all details for the QA Comparison Form
   */
  getById: async (qaReviewId: string): Promise<QaReviewDetail> => {
    return api.get<QaReviewDetail>(`/send-outs/qa-review/${qaReviewId}`);
  },

  /**
   * Update QA review (save draft)
   */
  update: async (
    qaReviewId: string,
    data: QaReviewUpdateData
  ): Promise<QaReviewDetail["qaReview"]> => {
    return api.put<QaReviewDetail["qaReview"]>(
      `/send-outs/qa-review/${qaReviewId}`,
      data
    );
  },

  /**
   * Complete QA review with validation
   */
  complete: async (
    qaReviewId: string,
    data: QaReviewCompleteData
  ): Promise<QaReviewDetail["qaReview"]> => {
    return api.post<QaReviewDetail["qaReview"]>(
      `/send-outs/qa-review/${qaReviewId}/complete`,
      data
    );
  },
};

// ==================== SHIPPING ====================

export type ShippingEligibility = "ready" | "blocked" | "not_required";

export type ShippingBlockReason =
  | "none"
  | "remote_address_missing"
  | "remote_address_invalid"
  | "tc_address_missing";

export type DestinationSource =
  | "tc_case_fields"
  | "surgery_center"
  | "remote_pathologist_user"
  | "remote_physician";

export interface ShippingQueueCase {
  id: string;
  caseNumber: string;
  accessioningType: string | null;
  patient: { firstName: string; lastName: string } | null;
  pathologist: {
    id: string;
    fullName: string;
    isRemote: boolean;
    remoteAddress: string | null;
  } | null;
  physician: {
    id: string;
    firstName: string;
    lastName: string;
    isRemote: boolean;
    remoteShippingAddress: string | null;
  } | null;
  originatingSurgeryCenter: { name: string; address: string | null } | null;
  sendingPractice: { name: string } | null;
  currentStatus: { name: string } | null;
  shippingPathologist: string | null;
  shippingStreet: string | null;
  shippingCity: string | null;
  shippingState: string | null;
  shippingZipCode: string | null;
  createdAt: string;
  shippingEligibility?: ShippingEligibility;
  shippingBlockReason?: ShippingBlockReason;
  shippingBlockMessage?: string | null;
  destinationSource?: DestinationSource;
}

export interface PendingShippingCase extends ShippingQueueCase {
  blockCount: number;
  slideCount: number;
}

export interface ConfirmShippingResult {
  success: boolean;
  confirmedCount: number;
  skippedCases: string[];
  blockedCases?: Array<{
    caseNumber: string;
    shippingBlockReason: ShippingBlockReason;
    shippingBlockMessage: string | null;
  }>;
}

export interface ShippingFilterOption {
  value: string;
  label: string;
}

export interface ShippedCase extends ShippingQueueCase {
  shippedAt: string;
  shippingTrackingNumber: string | null;
}

export interface ShippedCasesFilters {
  page: number;
  pageSize: number;
  search?: string;
  caseType?: "remote" | "tc_return";
  pathologists?: string[];
  sendingPractices?: string[];
  dateShippedFrom?: DateOnlyString;
  dateShippedTo?: DateOnlyString;
}

export interface ShippedCasesResponse {
  items: ShippedCase[];
  total: number;
  page: number;
  pageSize: number;
  facets: {
    caseTypes: ShippingFilterOption[];
    pathologists: ShippingFilterOption[];
    sendingPractices: ShippingFilterOption[];
  };
}

export interface ShippingTrackingUpdate {
  caseId: string;
  shippingTrackingNumber: string | null;
}

export const shippingService = {
  getPendingCases: async (): Promise<PendingShippingCase[]> => {
    return api.get<PendingShippingCase[]>("/workflows/shipping/pending");
  },

  confirmShipping: async (
    caseIds: string[],
    trackingNumber?: string
  ): Promise<ConfirmShippingResult> => {
    return api.post<ConfirmShippingResult>("/workflows/shipping/confirm", {
      caseIds,
      ...(trackingNumber !== undefined && { trackingNumber }),
    });
  },

  getShippedCases: async (
    filters: ShippedCasesFilters
  ): Promise<ShippedCasesResponse> => {
    const params = new URLSearchParams({
      page: String(filters.page),
      pageSize: String(filters.pageSize),
    });
    if (filters.search) params.set("search", filters.search);
    if (filters.caseType) params.set("caseType", filters.caseType);
    if (filters.pathologists?.length) {
      params.set("pathologists", filters.pathologists.join(","));
    }
    if (filters.sendingPractices?.length) {
      params.set("sendingPractices", filters.sendingPractices.join(","));
    }
    if (filters.dateShippedFrom) {
      params.set("dateShippedFrom", filters.dateShippedFrom);
    }
    if (filters.dateShippedTo) {
      params.set("dateShippedTo", filters.dateShippedTo);
    }
    return api.get<ShippedCasesResponse>(
      `/workflows/shipping/shipped?${params.toString()}`
    );
  },

  updateShippedCaseTracking: async (
    caseId: string,
    trackingNumber: string | null
  ): Promise<ShippingTrackingUpdate> => {
    return api.patch<ShippingTrackingUpdate>(
      `/workflows/shipping/shipped/${caseId}/tracking`,
      { trackingNumber }
    );
  },
};

// ==================== ERROR REPORTS ====================

export interface ErrorReportFilters {
  status?: "pending" | "resolved";
  categories?: string[];
  blocking?: "blocking" | "non-blocking";
  fromDate?: DateOnlyString;
  toDate?: DateOnlyString;
  search?: string;
  limit?: number;
}

export interface ErrorReport {
  id: string;
  caseId: string;
  case?: {
    id: string;
    caseNumber: string;
    parentCase?: { id: string; caseNumber: string } | null;
  };
  discrepancy?: {
    id: string;
    error: string;
    description: string;
    category: string;
  };
  // Alias for backwards compatibility
  error?: {
    id: string;
    error: string;
    description: string;
    category: string;
  };
  description: string;
  blockCase: boolean;
  preventSignOff: boolean;
  status: "pending" | "resolved";
  reportedAt: string;
  reportedBy?: {
    id: string;
    fullName: string;
  };
  reportedByUser?: {
    id: string;
    fullName: string;
  };
  resolvedAt?: string;
  resolvedBy?: {
    id: string;
    fullName: string;
  };
  resolutionNotes?: string;
  correctionRequestId?: string | null;
  correctionRequest?: { id?: string; refNumber?: number };
  refNumber?: number;
}

export const errorReportsService = {
  getReports: async (filters?: ErrorReportFilters): Promise<ErrorReport[]> => {
    const params = new URLSearchParams();
    const dateRange = normalizeDateOnlyQueryRange(
      { from: filters?.fromDate, to: filters?.toDate },
      { from: "fromDate", to: "toDate" }
    );

    if (filters?.status) {
      params.append("status", filters.status);
    }
    if (filters?.categories && filters.categories.length > 0) {
      params.append("categories", filters.categories.join(","));
    }
    if (filters?.blocking) {
      params.append("blocking", filters.blocking);
    }
    if (dateRange.from) {
      params.append("fromDate", dateRange.from);
    }
    if (dateRange.to) {
      params.append("toDate", dateRange.to);
    }
    if (filters?.search) {
      params.append("search", filters.search);
    }
    if (filters?.limit !== undefined) {
      params.append("limit", String(filters.limit));
    }

    const queryString = params.toString();
    const url = queryString
      ? `/workflows/errors?${queryString}`
      : "/workflows/errors";
    return api.get<ErrorReport[]>(url);
  },

  // Keep old method for backwards compatibility
  getPendingReports: async (): Promise<ErrorReport[]> => {
    return api.get<ErrorReport[]>("/workflows/errors?status=pending");
  },

  createReport: async (data: {
    caseId: string;
    errorId: string;
    description: string;
    blockCase?: boolean;
  }): Promise<ErrorReport> => {
    return api.post("/workflows/errors", data);
  },

  resolveReport: async (
    errorId: string,
    data: { resolutionNotes: string; correctionRequestId?: string | null }
  ): Promise<ErrorReport> => {
    return api.post(`/workflows/errors/${errorId}/resolve`, data);
  },

  sendEmail: async (
    errorId: string,
    data: any
  ): Promise<{ success: boolean }> => {
    return api.post(`/workflows/errors/${errorId}/email`, data);
  },
};

// ==================== CASE LOCK ====================

export const caseLockService = {
  acquireLock: async (
    caseId: string,
    workflowStep: string,
    reason?: string
  ): Promise<CaseLockResult> => {
    return api.post<CaseLockResult>(`/workflows/locks/${caseId}`, {
      workflowStep,
      reason,
    });
  },

  releaseLock: async (caseId: string): Promise<void> => {
    return api.delete(`/workflows/locks/${caseId}`);
  },

  forceReleaseLock: async (caseId: string): Promise<void> => {
    return api.delete(`/workflows/locks/${caseId}?force=true`);
  },

  extendLock: async (
    caseId: string
  ): Promise<{ success: boolean; expiresAt?: Date }> => {
    return api.put(`/workflows/locks/${caseId}`);
  },

  getLockInfo: async (caseId: string): Promise<any> => {
    return api.get(`/workflows/locks/${caseId}`);
  },

  getAllLocks: async (): Promise<any[]> => {
    return api.get(`/workflows/locks`);
  },
};

// ==================== BARCODE ====================

export const barcodeService = {
  openCaseByBarcode: async (barcode: string, intent?: string): Promise<any> => {
    return api.post("/workflows/barcode/parse", { barcode, intent });
  },
};

// ==================== ADMIN WORKFLOW MANAGEMENT ====================

export interface Workflow {
  id: string;
  name?: string;
  accessioningType?: string;
  description?: string;
  isActive?: boolean;
  statusCount?: number;
}

export interface CaseStatus {
  id: string;
  name: string;
  description?: string;
}

export interface CaseStatusWithUsage extends CaseStatus {
  workflowCount: number;
}

export interface WorkflowCaseStatus {
  id: string;
  name: string;
  description?: string;
  workflowId?: string;
  orderSequence?: number;
  isInitialStatus?: boolean;
  isFinalStatus?: boolean;
  caseStatus?: CaseStatus;
}

export interface StatusWorkflowMapping {
  workflowId: string;
  workflowType: string;
  workflowDescription: string;
  orderPosition: number;
  isInitial: boolean;
  isFinal: boolean;
}

export const workflowsService = {
  getAll: async (): Promise<Workflow[]> => {
    return api.get<Workflow[]>("/admin/workflows");
  },

  getCaseStatuses: async (): Promise<CaseStatusWithUsage[]> => {
    return api.get<CaseStatusWithUsage[]>("/admin/case-statuses");
  },

  getCaseStatusesForFiltering: async (): Promise<
    { id: string; name: string }[]
  > => {
    return api.get<{ id: string; name: string }[]>(
      "/admin/case-statuses/for-filtering"
    );
  },

  getWorkflowStatuses: async (
    workflowId: string
  ): Promise<WorkflowCaseStatus[]> => {
    return api.get<WorkflowCaseStatus[]>(
      `/admin/workflows/${workflowId}/statuses`
    );
  },

  getWorkflowsForCaseStatus: async (
    caseStatusId: string
  ): Promise<StatusWorkflowMapping[]> => {
    return api.get<StatusWorkflowMapping[]>(
      `/admin/case-statuses/${caseStatusId}/workflows`
    );
  },
};

// ==================== CONTROL CONFIRMATION ====================

export interface ControlConfirmationStatus {
  confirmed: boolean;
  confirmedAt?: string;
  confirmedBy?: string;
}

export interface HeConfirmationDetail {
  confirmed: boolean;
  confirmedAt?: string;
  confirmedBy?: string;
  practices: Array<{
    id: string;
    name: string;
    isConfirmed: boolean;
  }>;
  /** Whether the queried date is "today" in the business timezone (server-side) */
  isToday: boolean;
}

export interface PracticeForConfirmation {
  id: string;
  name: string;
}

export const controlConfirmationService = {
  getStatus: async (): Promise<ControlConfirmationStatus> => {
    return api.get<ControlConfirmationStatus>(
      "/workflows/control-confirmation/status"
    );
  },

  getDetailedStatus: async (date?: string): Promise<HeConfirmationDetail> => {
    const params = date ? `?date=${date}` : "";
    return api.get<HeConfirmationDetail>(
      `/workflows/control-confirmation/status${params}`
    );
  },

  getPractices: async (): Promise<PracticeForConfirmation[]> => {
    return api.get<PracticeForConfirmation[]>(
      "/workflows/control-confirmation/practices"
    );
  },

  confirm: async (practiceIds: string[]): Promise<any> => {
    return api.post("/workflows/control-confirmation", { practiceIds });
  },
};

// ==================== CONTROL SLIDES LIST ====================

export type PresenceStatus = "pending" | "confirmed";
export type ControlResult = "pass" | "fail";

/** Standalone per-stain+pool+day control record (new model for non-H&E stains) */
export interface StainOfDayGroup {
  controlRecordId: string | null;
  poolId: string | null;
  poolName: string;
  stainId: string;
  stainCode: string;
  stainName: string;
  presenceStatus: PresenceStatus;
  presenceConfirmedAt: string | null;
  presenceConfirmedByName: string | null;
  scannedId: string | null;
  result: ControlResult | null;
  reviewedAt: string | null;
  reviewedByName: string | null;
}

export interface ControlSlideSummary {
  /** Stains not yet confirmed present (for distribution badge) */
  pendingPresenceCount: number;
  /** Confirmed stains awaiting pathologist pass/fail (for pathologist badge) */
  pendingResultCount: number;
}

/** Legacy slide-level record — still used by distribution for individual slide display */
export interface ControlSlide {
  id: string;
  caseId: string;
  specimenId: string;
  blockId: string;
  number: string;
  stainId: string;
  isControlSlide: boolean;
  controlForPracticeId: string | null;
  printedAt: string | null;
  createdAt: string;
  case?: {
    id: string;
    caseNumber: string;
    patient?: {
      firstName: string;
      lastName: string;
      dateOfBirth: string;
    };
    currentStatus?: {
      id: string;
      name: string;
    };
  };
  stain?: {
    id: string;
    code: string;
    description: string;
  };
  controlForPractice?: {
    id: string;
    name: string;
  } | null;
  block?: {
    id: string;
    number: string;
  };
  specimen?: {
    id: string;
    number: string;
    source?: { id: string; name: string } | null;
    subsite?: { id: string; name: string } | null;
  };
}

export interface ControlSlidesFilters {
  date?: string;
  stainId?: string;
  practiceId?: string;
}

export const controlSlidesService = {
  /** Get legacy slide-level control slides list (used by distribution) */
  getControlSlides: async (
    filters: ControlSlidesFilters
  ): Promise<ControlSlide[]> => {
    const params = new URLSearchParams();
    if (filters.date) params.append("date", filters.date);
    if (filters.stainId) params.append("stainId", filters.stainId);
    if (filters.practiceId) params.append("practiceId", filters.practiceId);
    const queryString = params.toString();
    return api.get<ControlSlide[]>(
      `/workflows/control-slides${queryString ? `?${queryString}` : ""}`
    );
  },

  /** Get non-H&E stains used today with standalone presence + result status */
  getStainsOfDay: async (date?: string): Promise<StainOfDayGroup[]> => {
    const params = new URLSearchParams();
    if (date) params.append("date", date);
    const queryString = params.toString();
    return api.get<StainOfDayGroup[]>(
      `/workflows/control-slides/stains-of-day${
        queryString ? `?${queryString}` : ""
      }`
    );
  },

  /** Get summary counts of pending presence + pending result for today */
  getControlSummary: async (date?: string): Promise<ControlSlideSummary> => {
    const params = new URLSearchParams();
    if (date) params.append("date", date);
    const queryString = params.toString();
    return api.get<ControlSlideSummary>(
      `/workflows/control-slides/summary${queryString ? `?${queryString}` : ""}`
    );
  },

  /** Confirm a control slide is present/prepared */
  confirmPresence: async (
    stainId: string,
    poolId: string | null,
    scannedId?: string
  ): Promise<void> => {
    return api.post<void>("/workflows/control-slides/confirm-presence", {
      stainId,
      poolId,
      scannedId,
    });
  },

  /** Undo presence confirmation */
  undoPresence: async (
    stainId: string,
    poolId: string | null
  ): Promise<void> => {
    return api.post<void>("/workflows/control-slides/undo-presence", {
      stainId,
      poolId,
    });
  },

  /** Set pass/fail result (pathologist action) */
  setResult: async (
    stainId: string,
    poolId: string | null,
    result: ControlResult
  ): Promise<void> => {
    return api.post<void>("/workflows/control-slides/set-result", {
      stainId,
      poolId,
      result,
    });
  },

  /** Undo pass/fail result */
  undoResult: async (stainId: string, poolId: string | null): Promise<void> => {
    return api.post<void>("/workflows/control-slides/undo-result", {
      stainId,
      poolId,
    });
  },
};

// ==================== QA METRICS REPORTING ====================

export interface QaMetricsFilters {
  fromDate?: string;
  toDate?: string;
  pathologistId?: string;
  institutionId?: string;
}

export interface QaMetricsSummary {
  totalSentOut: number;
  totalReturned: number;
  qaReviewsCompleted: number;
  qaReviewsPending: number;
  agreementBreakdown: {
    complete: number;
    minorVariation: number;
    partial: number;
    discrepancy: number;
    majorDiscrepancy: number;
  };
  actionBreakdown: {
    none: number;
    notifyPathologist: number;
    internalDiscussion: number;
    amendmentNeeded: number;
  };
  correctedReportRate: number;
  averageTurnaroundDays: number;
}

export interface QaMetricsTrendPoint {
  month: string;
  complete: number;
  minorVariation: number;
  partial: number;
  discrepancy: number;
  majorDiscrepancy: number;
  total: number;
}

export interface QaMetricsByPathologist {
  pathologistId: string;
  pathologistName: string;
  totalReviews: number;
  complete: number;
  minorVariation: number;
  partial: number;
  discrepancy: number;
  majorDiscrepancy: number;
  agreementRate: number;
}

export interface QaMetricsByInstitution {
  institutionId: string;
  institutionName: string;
  totalReviews: number;
  complete: number;
  minorVariation: number;
  partial: number;
  discrepancy: number;
  majorDiscrepancy: number;
  agreementRate: number;
}

export interface QaMetricsExportRow {
  caseNumber: string;
  patientName: string;
  originalPathologist: string;
  qaReviewer: string;
  institution: string;
  agreementLevel: string;
  recommendedAction: string;
  comments: string;
  createdAt: string;
  completedAt: string;
}

function buildQaMetricsParams(filters?: QaMetricsFilters): string {
  if (!filters) return "";
  const params = new URLSearchParams();
  if (filters.fromDate) params.append("fromDate", filters.fromDate);
  if (filters.toDate) params.append("toDate", filters.toDate);
  if (filters.pathologistId)
    params.append("pathologistId", filters.pathologistId);
  if (filters.institutionId)
    params.append("institutionId", filters.institutionId);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const qaMetricsService = {
  getSummary: async (filters?: QaMetricsFilters): Promise<QaMetricsSummary> => {
    return api.get<QaMetricsSummary>(
      `/workflows/qa-metrics/summary${buildQaMetricsParams(filters)}`
    );
  },

  getTrends: async (
    filters?: QaMetricsFilters
  ): Promise<QaMetricsTrendPoint[]> => {
    return api.get<QaMetricsTrendPoint[]>(
      `/workflows/qa-metrics/trends${buildQaMetricsParams(filters)}`
    );
  },

  getByPathologist: async (
    filters?: QaMetricsFilters
  ): Promise<QaMetricsByPathologist[]> => {
    return api.get<QaMetricsByPathologist[]>(
      `/workflows/qa-metrics/by-pathologist${buildQaMetricsParams(filters)}`
    );
  },

  getByInstitution: async (
    filters?: QaMetricsFilters
  ): Promise<QaMetricsByInstitution[]> => {
    return api.get<QaMetricsByInstitution[]>(
      `/workflows/qa-metrics/by-institution${buildQaMetricsParams(filters)}`
    );
  },

  exportData: async (
    filters?: QaMetricsFilters
  ): Promise<QaMetricsExportRow[]> => {
    return api.get<QaMetricsExportRow[]>(
      `/workflows/qa-metrics/export${buildQaMetricsParams(filters)}`
    );
  },
};

// ==================== REPORT DELIVERY QUEUE ====================

export interface DeliveryQueueFilters {
  status?: DeliveryQueueStatus[];
  deliveryMethod?: string[];
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  caseId?: string;
  failedOnly?: boolean;
}

export type DeliveryQueueView = "active" | "delivered" | "cancelled";

export interface DeliveryQueuePageRequest extends DeliveryQueueFilters {
  view: DeliveryQueueView;
  page: number;
  pageSize: number;
}

export type DeliveryQueueStatus =
  | "pending"
  | "recalled"
  | "deferred"
  | "held"
  | "sending"
  | "ready_for_print"
  | "ready_for_pickup"
  | "sent"
  | "delivered"
  | "bounced"
  | "printed"
  | "mailed"
  | "failed"
  | "cancelled";

export type DeliveryRecallAttemptOutcome =
  | "recalled"
  | "partial"
  | "unavailable"
  | "already_recalled"
  | "version_changed"
  | "denied";

export type DeliveryRecallDestinationOutcome =
  | "recalled"
  | "already_recalled"
  | "dispatch_started"
  | "already_sent"
  | "failed"
  | "cancelled"
  | "superseded"
  | "legacy_unbound"
  | "unavailable";

export interface DeliveryRecallOutcomeEvidence {
  queueItemId: string;
  recipientName: string | null;
  destination: string | null;
  deliveryMethodName: string | null;
  scheduledAt: string | null;
  statusBefore: DeliveryQueueStatus;
  statusAfter: DeliveryQueueStatus;
  result: DeliveryRecallDestinationOutcome;
}

export interface DeliveryRecallHistorySummary {
  attemptId: string;
  attemptedAt: string;
  attemptOutcome: DeliveryRecallAttemptOutcome;
  authorityBasis: "assigned_pathologist" | "any_case_capability" | "denied";
  reportVersion: number | null;
  actorName: string;
  reason: string | null;
  deliveryOutcome: DeliveryRecallOutcomeEvidence;
}

export interface DeliveryQueueItem {
  id: string;
  caseId: string;
  caseNumber: string;
  recipientType:
    | "physician"
    | "practice"
    | "surgery_center"
    | "lab"
    | "unknown";
  recipientName: string;
  deliveryMethod: string;
  deliveryMethodId: string;
  /** True when the worker is configured not to process this delivery method. */
  deliverySuppressed: boolean;
  destination: string;
  status: DeliveryQueueStatus;
  signOffId?: string | null;
  reportVersion?: number | null;
  canCancel?: boolean;
  canRetry?: boolean;
  isSuperseded?: boolean;
  recallOutcome?: DeliveryRecallDestinationOutcome;
  recallHistory?: DeliveryRecallHistorySummary[];
  scheduledAt: string;
  sentAt?: string;
  deliveredAt?: string;
  deliveryAttempts: number;
  lastError?: string;
  digestState?: "pending" | "sending" | "included" | null;
  digestSentAt?: string | null;
  heldEmailId?: string | null;
  createdAt: string;
}

export interface DeliveryQueueStats {
  pending: number;
  recalled: number;
  deferred: number;
  held: number;
  sending: number;
  delivered: number;
  failed: number;
  cancelled: number;
  total: number;
}

export interface DeliveryQueuePagination {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface DeliveryQueueMatchingCounts {
  total: number;
  active: number;
  delivered: number;
  cancelled: number;
}

export interface DeliveryQueuePage {
  items: DeliveryQueueItem[];
  pagination: DeliveryQueuePagination;
  matching: DeliveryQueueMatchingCounts;
}

export interface BulkActionResult {
  success: number;
  failed: number;
  errors: string[];
}

export interface BulkDeliveryFilters {
  practiceIds?: string[];
  surgeryCenterIds?: string[];
  sendingLabIds?: string[];
  physicianIds?: string[];
  caseNumber?: string;
}

export interface BulkDeliveryMethodOption {
  configId: string;
  deliveryMethodId: string;
  deliveryMethodName: string;
  recipientType: "physician" | "practice" | "surgery_center" | "lab";
  entityId: string;
  entityName: string;
  destination: string | null;
  email: string | null;
  fax: string | null;
}

export interface BulkDeliveryRecipient {
  key: string;
  type: BulkDeliveryMethodOption["recipientType"];
  entityId: string;
  name: string;
  methods: BulkDeliveryMethodOption[];
}

export interface BulkDeliveryCandidate {
  id: string;
  caseNumber: string;
  signOffId: string;
  reportVersion: number;
  recipients: BulkDeliveryRecipient[];
}

export interface BulkDeliveryPlan {
  planId: string;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  selectedCaseIds: string[];
  items: BulkDeliveryCandidate[];
  recipients: BulkDeliveryRecipient[];
}

export interface BulkDeliveryPlanRequest {
  filters: BulkDeliveryFilters;
  page?: number;
  pageSize?: number;
  planId?: string;
  selectedCaseIds?: string[];
  excludedCaseIds?: string[];
  selectedConfigIdsByEntity?: Record<string, string[]>;
}

export interface BulkDeliveryConfirmResult {
  batchId: string;
  replay: "created" | "already_confirmed";
  scheduledAt: string | null;
  queuedCount: number;
  outcomes: Array<{
    caseId: string;
    caseNumber: string;
    status: string;
    entities: Array<{
      entityKey: string;
      entityName: string;
      status: string;
      queuedCount: number;
    }>;
  }>;
  requestHash?: string;
}

/** A delivery method the worker is currently configured not to process. */
export interface SuppressedDeliveryMethod {
  key: string;
  label: string;
}

export interface DeliverySuppressionSettings {
  /** Methods currently held — no deliveries are sent for these. */
  methods: SuppressedDeliveryMethod[];
  /** Every active delivery method, i.e. what may be added to the hold list. */
  available: SuppressedDeliveryMethod[];
}

export interface DeliverySuppressionUpdateResult
  extends DeliverySuppressionSettings {
  changed: boolean;
}

export const reportDeliveryQueueService = {
  getQueue: async (
    request: DeliveryQueuePageRequest
  ): Promise<DeliveryQueuePage> => {
    const params = new URLSearchParams();
    params.set("view", request.view);
    params.set("page", String(request.page));
    params.set("pageSize", String(request.pageSize));
    if (request.status?.length) {
      params.append("status", request.status.join(","));
    }
    if (request.deliveryMethod?.length) {
      params.append("deliveryMethod", request.deliveryMethod.join(","));
    }
    if (request.dateFrom) params.append("dateFrom", request.dateFrom);
    if (request.dateTo) params.append("dateTo", request.dateTo);
    if (request.search) params.append("search", request.search);
    if (request.caseId) params.append("caseId", request.caseId);
    if (request.failedOnly) params.append("failedOnly", "true");

    return api.get<DeliveryQueuePage>(
      `/workflows/delivery-queue/v2?${params.toString()}`
    );
  },

  getStats: async (): Promise<DeliveryQueueStats> => {
    return api.get<DeliveryQueueStats>("/workflows/delivery-queue/stats");
  },

  planBulkDelivery: async (
    request: BulkDeliveryPlanRequest
  ): Promise<BulkDeliveryPlan> => {
    return api.post<BulkDeliveryPlan>(
      "/workflows/delivery-queue/bulk/plan",
      request
    );
  },

  confirmBulkDelivery: async (
    planId: string,
    idempotencyKey: string,
    selectedConfigIdsByEntity: Record<string, string[]>
  ): Promise<BulkDeliveryConfirmResult> => {
    return api.post<BulkDeliveryConfirmResult>(
      "/workflows/delivery-queue/bulk/confirm",
      { planId, idempotencyKey, selectedConfigIdsByEntity }
    );
  },

  getSuppressedMethods: async (): Promise<DeliverySuppressionSettings> => {
    const response = await api.get<DeliverySuppressionSettings>(
      "/workflows/delivery-queue/suppressed-methods"
    );
    return {
      methods: response.methods ?? [],
      available: response.available ?? [],
    };
  },

  updateSuppressedMethods: async (
    methodKeys: string[]
  ): Promise<DeliverySuppressionUpdateResult> => {
    return api.put<DeliverySuppressionUpdateResult>(
      "/workflows/delivery-queue/suppressed-methods",
      { methodKeys }
    );
  },

  bulkCancel: async (deliveryIds: string[]): Promise<BulkActionResult> => {
    return api.post<BulkActionResult>("/workflows/delivery-queue/cancel", {
      deliveryIds,
    });
  },

  bulkRetry: async (deliveryIds: string[]): Promise<BulkActionResult> => {
    return api.post<BulkActionResult>("/workflows/delivery-queue/retry", {
      deliveryIds,
    });
  },

  bulkResend: async (deliveryIds: string[]): Promise<BulkActionResult> => {
    return api.post<BulkActionResult>("/workflows/delivery-queue/resend", {
      deliveryIds,
    });
  },

  processNow: async (): Promise<{ message: string; queued: number }> => {
    return api.post<{ message: string; queued: number }>(
      "/workflows/delivery-queue/process",
      {}
    );
  },

  processSelected: async (
    deliveryIds: string[]
  ): Promise<{ message: string; queued: number }> => {
    return api.post<{ message: string; queued: number }>(
      "/workflows/delivery-queue/process-selected",
      { deliveryIds }
    );
  },
};

// ==================== STAIN METRICS REPORTING ====================

export interface StainMetricsFilters {
  fromDate?: string;
  toDate?: string;
  stainType?: string;
  facilityId?: string;
}

export interface TurnaroundByStainType {
  stainType: string;
  avgTatHours: number;
  totalSlides: number;
  completedSlides: number;
  targetHours: number;
  withinTarget: number;
  withinTargetPct: number;
}

export interface RushVolume {
  total: number;
  rushCount: number;
  highPriorityCount: number;
  rushPct: number;
}

export interface QcFailureRate {
  totalSlides: number;
  failedSlides: number;
  failureRate: number;
  failuresByStainType: Array<{
    stainType: string;
    count: number;
    rate: number;
  }>;
}

export interface SendOutTurnaround {
  providerId: string;
  providerName: string;
  avgTatDays: number;
  expectedTatDays: number;
  totalSendOuts: number;
  completedSendOuts: number;
  overdueCount: number;
}

export interface StainMetricsSummary {
  turnaroundByStainType: TurnaroundByStainType[];
  rushVolume: RushVolume;
  qcFailureRate: QcFailureRate;
  sendOutTurnaround: SendOutTurnaround[];
}

export interface StainMetricsTrendPoint {
  month: string;
  tatByType: Record<string, number>;
  rushCount: number;
  qcFailureRate: number;
  sendOutAvgDays: number;
}

export interface StainMetricsTrends {
  months: StainMetricsTrendPoint[];
}

function buildStainMetricsParams(filters?: StainMetricsFilters): string {
  if (!filters) return "";
  const params = new URLSearchParams();
  if (filters.fromDate) params.append("fromDate", filters.fromDate);
  if (filters.toDate) params.append("toDate", filters.toDate);
  if (filters.stainType) params.append("stainType", filters.stainType);
  if (filters.facilityId) params.append("facilityId", filters.facilityId);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const stainMetricsService = {
  getSummary: async (
    filters?: StainMetricsFilters
  ): Promise<StainMetricsSummary> => {
    return api.get<StainMetricsSummary>(
      `/workflows/stain-metrics/summary${buildStainMetricsParams(filters)}`
    );
  },

  getTrends: async (
    filters?: StainMetricsFilters
  ): Promise<StainMetricsTrends> => {
    return api.get<StainMetricsTrends>(
      `/workflows/stain-metrics/trends${buildStainMetricsParams(filters)}`
    );
  },
};

export const userPreferencesService = {
  get: async (key: string): Promise<Record<string, any> | null> => {
    return api.get(`/users/me/preferences/${key}`);
  },
  save: async (key: string, value: Record<string, any>): Promise<void> => {
    return api.put(`/users/me/preferences/${key}`, { value });
  },
};
